/* eslint-disable @typescript-eslint/no-explicit-any */

import { HeaderCell } from "@/components/ui/table";
import { SortConfig } from "@/hooks/useTableServer";
import dayjs from "dayjs";
import {
  ECreditDeductionCategory,
  ECreditAddCategory,
  EUserAdCreditAction,
  IUserAdCreditCollapseHistory,
} from "@/utilities/types/AdCredit";
import formatNumberDecimal from "@/utils/format-number-decimal";
import { DEFAULT_REASON_CREDIT } from "@/config/constants";
import { capitalizeWords } from "@/utilities/functions/capitalizeWords";
import { Button, Select, Popover, Title, Text, Input } from "rizzui";
import AdCreditRepository from "@/utilities/repositories/AdCredit";
import toast from "react-hot-toast";
import { useState } from "react";
import {
  PiArrowCounterClockwise,
  PiPencilSimple,
  PiCheck,
  PiX,
} from "react-icons/pi";
import useProfile from "@/hooks/use-profile";
import { Roles } from "@/utilities/types/Users";
import { useNavigate } from "react-router-dom";

type UserAdCreditSortBy = "createdAt";

export const getColumns = (
  sortConfig?: SortConfig<UserAdCreditSortBy>,
  onHeaderCellClick?: (value: string) => void,
  handleRefreshHistory?: () => void,
) => {
  const navigate = useNavigate();
  const [loadingTransactionId, setLoadingTransactionId] = useState<
    string | null
  >(null);
  const [updatingCategoryId, setUpdatingCategoryId] = useState<string | null>(
    null,
  );
  const [editingDescriptionId, setEditingDescriptionId] = useState<
    string | null
  >(null);
  const [descriptionValue, setDescriptionValue] = useState<string>("");
  const [updatingDescriptionId, setUpdatingDescriptionId] = useState<
    string | null
  >(null);
  const { profile } = useProfile();

  const handleRevert = async (
    transactionId: string,
    setOpen: (open: boolean) => void,
  ) => {
    const toastId = toast.loading("Reverting transaction...");
    setLoadingTransactionId(transactionId);

    try {
      await AdCreditRepository.revertAdCreditWithTransactionId(transactionId);
      toast.success("Transaction reverted successfully", { id: toastId });
      setOpen(false);
      handleRefreshHistory?.();
    } catch (error: any) {
      toast.error(error?.message || "Failed to revert transaction", {
        id: toastId,
      });
    } finally {
      setLoadingTransactionId(null);
    }
  };

  const handleUpdateCategory = async (
    transactionId: string,
    newCategory: ECreditDeductionCategory | ECreditAddCategory,
  ) => {
    const toastId = toast.loading("Updating category...");
    setUpdatingCategoryId(transactionId);

    try {
      await AdCreditRepository.updateTransactionCategory(transactionId, {
        category: newCategory,
      });
      toast.success("Category updated successfully", { id: toastId });
      handleRefreshHistory?.();
    } catch (error: any) {
      toast.error(error?.message || "Failed to update category", {
        id: toastId,
      });
    } finally {
      setUpdatingCategoryId(null);
    }
  };

  const handleStartEditDescription = (
    transactionId: string,
    currentReason: string,
  ) => {
    setEditingDescriptionId(transactionId);
    setDescriptionValue(currentReason || "");
  };

  const handleCancelEditDescription = () => {
    setEditingDescriptionId(null);
    setDescriptionValue("");
  };

  const handleSaveDescription = async (transactionId: string) => {
    const toastId = toast.loading("Updating description...");
    setUpdatingDescriptionId(transactionId);

    try {
      await AdCreditRepository.updateTransactionCategory(transactionId, {
        reason: descriptionValue,
      });
      toast.success("Description updated successfully", { id: toastId });
      setEditingDescriptionId(null);
      setDescriptionValue("");
      handleRefreshHistory?.();
    } catch (error: any) {
      toast.error(error?.message || "Failed to update description", {
        id: toastId,
      });
    } finally {
      setUpdatingDescriptionId(null);
    }
  };
  const viewLead = (leadId?: string) => {
    if (!leadId) return;
    navigate(`/enquiry-entries?search=&page=1&limit=10&leadId=${leadId}`);
  };

  return [
    {
      title: (
        <HeaderCell
          title="Date"
          align="left"
          sortable
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "createdAt"
          }
          // descending={
          //   sortConfig?.direction === "desc" && sortConfig?.key === "updatedAt"
          // }
        />
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      onHeaderCell: () => ({
        onClick: () => onHeaderCellClick?.("createdAt"),
      }),
      render: (createdAt: string) => {
        return (
          <span className="font-medium">
            {dayjs(new Date(createdAt)).format("DD/MM/YYYY")}
          </span>
        );
      },
    },

    {
      title: <HeaderCell title="Credit in" align="left" />,
      dataIndex: "credit-in",
      key: "userAdCreditData",
      align: "left",
      render: (_: any, row: IUserAdCreditCollapseHistory) => {
        if (row?.action === EUserAdCreditAction.DEDUCT) return "";
        return <span>{formatNumberDecimal(Number(row?.amount || 0))}</span>;
      },
    },

    {
      title: <HeaderCell title="Credit out" align="left" />,
      dataIndex: "credit-out",
      key: "userAdCreditData",
      align: "left",
      render: (_: any, row: IUserAdCreditCollapseHistory) => {
        if (row?.action === EUserAdCreditAction.ADD) return "";
        return <span>{formatNumberDecimal(Number(row?.amount || 0))}</span>;
      },
    },

    {
      title: <HeaderCell title="Category" align="left" />,
      dataIndex: "category",
      key: "userAdCreditData",
      align: "left",
      render: (_: any, row: IUserAdCreditCollapseHistory) => {
        const category = row?.histories?.[0]?.category;
        const isUpdating = updatingCategoryId === row.transactionID;

        // Get display text for category
        let displayCategory = "";
        if (row?.action === EUserAdCreditAction.ADD) {
          if (category === ECreditAddCategory.REFUND)
            displayCategory = "Refund";
          else if (category === ECreditAddCategory.BONUS)
            displayCategory = "Bonus";
          else if (category === ECreditAddCategory.TOP_UP)
            displayCategory = "Top-Up";
        } else {
          if (category === ECreditDeductionCategory.MISCELLANEOUS)
            displayCategory = "Ancillary";
          else
            displayCategory = capitalizeWords(
              category?.replace("-", " ") || "",
            );
        }

        // Only show edit for ADMIN role
        if (profile?.role !== Roles.ADMIN) {
          return <span>{displayCategory}</span>;
        }

        // Category options based on action type
        const categoryOptions: Array<{
          value: ECreditDeductionCategory | ECreditAddCategory;
          label: string;
        }> =
          row?.action === EUserAdCreditAction.ADD
            ? [
                { value: ECreditAddCategory.TOP_UP, label: "Top-Up" },
                {
                  value: ECreditAddCategory.REFUND,
                  label: "Google Ads Refund",
                },
                { value: ECreditAddCategory.BONUS, label: "Bonus" },
              ]
            : [
                {
                  value: ECreditDeductionCategory.GOOGLE_ADS,
                  label: "Google Ads",
                },
                { value: ECreditDeductionCategory.FACEBOOK, label: "Facebook" },
                { value: ECreditDeductionCategory.LEAD, label: "Lead" },
                {
                  value: ECreditDeductionCategory.MISCELLANEOUS,
                  label: "Ancillary",
                },
              ];

        return (
          <Select
            options={categoryOptions}
            value={category}
            displayValue={(value) => {
              const option = categoryOptions.find((o) => o.value === value);
              return option ? option.label : "";
            }}
            onChange={(option: { value: string }) =>
              handleUpdateCategory(
                row.histories[0].id,
                (option && option.value) as
                  | ECreditDeductionCategory
                  | ECreditAddCategory,
              )
            }
            disabled={isUpdating}
            placeholder="Select category"
            className="w-40"
            size="sm"
          />
        );
      },
    },

    {
      title: <HeaderCell title="Description" align="left" />,
      dataIndex: "reason",
      key: "reason",
      align: "left",
      render: (reason: string, row: IUserAdCreditCollapseHistory) => {
        // Set default reason if none provided
        let displayReason: React.ReactNode = reason;
        const leadId = row.histories[0]?.contactSaleSubmissionId;
        if (!reason || reason.trim() === "") {
          displayReason =
            row?.action === EUserAdCreditAction.ADD
              ? DEFAULT_REASON_CREDIT.ADD
              : DEFAULT_REASON_CREDIT.DEDUCT;
        }

        const transactionId = row.histories[0].id;
        const isEditing = editingDescriptionId === transactionId;
        const isUpdating = updatingDescriptionId === transactionId;

        // Only show edit for ADMIN role
        if (profile?.role !== Roles.ADMIN) {
          return <span>{displayReason}</span>;
        }

        if (
          leadId &&
          typeof displayReason === "string" &&
          displayReason.includes("Lead SMS notification for")
        ) {
          const leadName = displayReason
            .split("Lead SMS notification for")[1]
            .trim();
          displayReason = (
            <span>
              Lead SMS notification for{" "}
              <span
                onClick={() => viewLead(leadId)}
                className="cursor-pointer underline"
              >
                {leadName}
              </span>
            </span>
          );
        }

        if (isEditing) {
          return (
            <div className="flex items-center gap-2">
              <Input
                value={descriptionValue}
                onChange={(e) => setDescriptionValue(e.target.value)}
                placeholder="Enter description"
                className="w-64"
                size="sm"
                disabled={isUpdating}
              />
              <Button
                size="sm"
                variant="flat"
                onClick={() => handleSaveDescription(transactionId)}
                disabled={isUpdating || descriptionValue.trim() === ""}
                className="px-2 disabled:text-gray-400"
              >
                <PiCheck className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="flat"
                onClick={handleCancelEditDescription}
                disabled={isUpdating}
                className="px-2"
              >
                <PiX className="h-4 w-4" />
              </Button>
            </div>
          );
        }

        return (
          <div className="flex items-center gap-2 group cursor-pointer">
            <span>{displayReason}</span>
            <Button
              size="sm"
              variant="text"
              onClick={() => handleStartEditDescription(transactionId, reason)}
              className="opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity px-1"
            >
              <PiPencilSimple className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },

    {
      title: <HeaderCell title="Expiry Date" align="left" />,
      dataIndex: "expired",
      key: "userAdCreditData",
      align: "left",
      render: (_: any, row: IUserAdCreditCollapseHistory) => {
        // const expiredAt = row?.userAdCreditData?.expiredAt;
        // return expiredAt ? dayjs(expiredAt).format("DD/MM/YYYY") : "No Expiry";
        const histories = row?.histories || [];
        const sorted = [...histories].sort((a, b) => {
          const aDate = a?.userAdCreditData?.expiredAt
            ? new Date(a.userAdCreditData.expiredAt).getTime()
            : null;
          const bDate = b?.userAdCreditData?.expiredAt
            ? new Date(b.userAdCreditData.expiredAt).getTime()
            : null;

          if (!aDate && !bDate) return 0;
          if (!aDate) return 1;
          if (!bDate) return -1;

          return aDate - bDate;
        });
        const expiredAt =
          sorted.length > 0 ? sorted[0].userAdCreditData?.expiredAt : "";
        return expiredAt ? dayjs(expiredAt).format("DD/MM/YYYY") : "No Expiry";
      },
    },

    profile?.role === Roles.ADMIN
      ? {
          title: <HeaderCell title="Action" align="center" />,
          dataIndex: "action",
          key: "action",
          align: "center",
          render: (_: any, row: IUserAdCreditCollapseHistory) => {
            const isLoading = loadingTransactionId === row.transactionID;
            const leadId = row.histories[0]?.contactSaleSubmissionId;

            return (
              <div className="flex justify-center gap-2 items-center">
                <Popover placement="left">
                  <Popover.Trigger>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isLoading || !row.canRevert}
                    >
                      Revert
                    </Button>
                  </Popover.Trigger>
                  <Popover.Content className="z-50">
                    {({ setOpen }) => (
                      <div className="w-56 pb-2 pt-1 text-left rtl:text-right">
                        <Title
                          as="h6"
                          className="mb-0.5 flex items-start text-sm text-gray-700 sm:items-center"
                        >
                          <PiArrowCounterClockwise className="me-1 h-[17px] w-[17px]" />
                          Revert Transaction
                        </Title>
                        <Text className="mb-2 leading-relaxed text-gray-500">
                          Are you sure you want to revert this transaction?
                        </Text>
                        <div className="flex items-center justify-end">
                          <Button
                            size="sm"
                            className="me-1.5 h-7"
                            isLoading={isLoading}
                            onClick={() =>
                              handleRevert(row.transactionID, setOpen)
                            }
                          >
                            Yes
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7"
                            onClick={() => setOpen(false)}
                            disabled={isLoading}
                          >
                            No
                          </Button>
                        </div>
                      </div>
                    )}
                  </Popover.Content>
                </Popover>
              </div>
            );
          },
        }
      : undefined,
  ];
};
